import { useEffect, useState } from "react";
import useApiCallService from "../../Common/Helpers/ApiCall";
import SortableList from "../../Common/Components/Lists/SortableList";
import { Button, Col } from "reactstrap";
import { ADMIN_PATHS } from "../../config";
import AccountingModel from "../Models/Response/AccountingModel";
import { useTranslation } from "react-i18next";
import { CheckUserRole } from "../../AppSystem/Helpers/CheckUserRole";
import { CheckUserPermission } from "../../AppSystem/Helpers/CheckUserPermission";
import { CustomModal } from "../../Common/Components/Modal/CustomModal";
import { CustomReload } from "../../AppSystem/Helpers/CustomReload";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import { useHeader } from "../../Auth/Components/Nav/HeaderContext";
import { SettingsListHeader } from "../../SystemSettings/Components/SettingsListHeader";
import { AccountingFormPanel } from "../Components/AccountingFormPanel";

const AdminAccountingList = () => {
    const isSuperAdmin = CheckUserRole("SuperAdmin");
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();
    const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
    const [deleteApiResponse, setDeleteApiResponse] = useState(null);
    const [accountingId, setAccountingId] = useState(null);
    const [items, setItems] = useState([]);
    // null = closed, "" = create, id = edit
    const [formPanelId, setFormPanelId] = useState(null);
    const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();

    useEffect(() => {
        setPageTitle(t('sidebar.accountingList'));
        setPageTitleKey('sidebar.accountingList');
        setOnBack(null);
        return () => { setPageTitle(''); setPageTitleKey(''); setOnBack(null); };
    }, [t]);

    let permissionUpdate = false;
    if (CheckUserPermission("system.setting.account-settings.update") || CheckUserPermission("system.setting.account-settings") || CheckUserPermission("system") || CheckUserPermission("system.setting")) {
        permissionUpdate = true;
    }

    let permissionDelete = false;
    if (CheckUserPermission("system.setting.account-settings") || CheckUserPermission("system.setting") || CheckUserPermission("system.setting.account-settings.delete") || CheckUserPermission("system")) {
        permissionDelete = true;
    }

    // Función para mover items arriba/abajo
    const handleMoveItem = async (itemId, direction) => {
        try {
            const apiCallOptions = {
                method: "put",
                url: `/accounting/reorder?id=${itemId}&direction=${direction}`,
            };
            await ApiCall(apiCallOptions, (apiResponse) => {
                if (apiResponse) {
                    //FlashMessage(t('validationMessage.orderUpdated'), 'flash-messageSuccess');
                    setTimeout(() => {
                        CustomReload(ADMIN_PATHS.ADMIN_ACCOUNTING);
                    }, 500);
                }
            });
        } catch (error) {
            console.log(error);
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
        }
    };

    const renderActionColumn = (item, totalItems) => {

        const isFirst = item.position === 1;
        const isLast = item.position === totalItems;

        return (
            <>
                {/* Botón Mover Arriba */}
                <Button 
                    className="bg-blue-light text-blue border-0 position-relative me-2 rounded" 
                    onClick={() => handleMoveItem(item.id, 'up')}
                    disabled={isFirst}
                >
                    <i className="bx bx-up-arrow-alt fs-5 align-middle pb-1" />
                    <div className="tooltip-custom-button">
                        {t("button.moveUp")}
                    </div>
                </Button>

                {/* Botón Mover Abajo */}
                <Button 
                    className="bg-blue-light text-blue border-0 position-relative me-2 rounded" 
                    onClick={() => handleMoveItem(item.id, 'down')}
                    disabled={isLast}
                >
                    <i className="bx bx-down-arrow-alt fs-5 align-middle pb-1" />
                    <div className="tooltip-custom-button">
                        {t("button.moveDown")}
                    </div>
                </Button>

                {/* Botón Eliminar */}
                {permissionDelete && (
                    <Button 
                        className="box-red border-0 position-relative me-2 rounded" 
                        onClick={() => {
                            setDisplayDeleteModal(true);
                            setAccountingId(item.id);
                        }}
                    >
                        <i className="bx bx-x-circle fs-5 align-middle pb-1" />
                        <div className="tooltip-custom-button">
                            {t("button.delete")}
                        </div>
                    </Button>
                )}

                {/* Botón Editar */}
                {permissionUpdate && (
                    <Button 
                        className="bg-green-light text-green border-0 position-relative" 
                        onClick={() => setFormPanelId(item.id)}
                    >
                        <i className="bx bxs-pencil fs-5 align-middle pb-1" />
                        <div className="tooltip-custom-button">
                            {t("button.edit")}
                        </div>
                    </Button>
                )}
            </>
        );
    }

    // HANDLE MODAL DELETE
    const handleModalDelete = async () => {
        const apiCallOptions = {
            method: "delete",
            url: `/accounting?id=${accountingId}`
        };
        try {
            await ApiCall(apiCallOptions, setDeleteApiResponse);
            setDisplayDeleteModal(false);
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (deleteApiResponse !== null) {
            CustomReload(ADMIN_PATHS.ADMIN_ACCOUNTING);
        }
    }, [deleteApiResponse]);

    const modalContentDelete = (
        <div>
            {`${t('validationMessage.modalDelete')}`}
        </div>
    );

    const modalFooterDelete = (
        <>
            <Button className="box-green border-0" onClick={handleModalDelete}>
                {t('button.delete')}
            </Button>
            <Button className="box-red border-0" onClick={() => setDisplayDeleteModal(false)}>
                {t('button.cancel')}
            </Button>
        </>
    );

    const columns = [
        //"position",
        "value",
        "name",
        "requiredDocumentation",
    ];

    const columnsToExport = [
        //"position",
        "value",
        "name",
        "requiredDocumentation",
    ];

    const listOptions = {
        "model": AccountingModel,
        "columns": columns,
        "actions": {
            "actionColumn": renderActionColumn,
        },
        "exportIsAvailable": true,
        "urlGetListItemsToExport": "/accounting/list-all",
        "nameFile": t('sidebar.accountingList'),
        "ignoreFields": columnsToExport,
        "modelType": "Accounting",
        "filterTestIsAvailable": true,
        "urlGetSearch": "/accounting/list-all",
        "viewName": "AdminAccounting",
        "modernDesign": true,
        // Account number column only as wide as needed, more room for the name
        "columnWeights": { "value": 0.3, "name": 1.6 },
        "mobileCard": {
            "title": (item) => item.name,
            "value": (item) => item.value,
            "valueCaption": AccountingModel.getAttributes().value.label,
            "badges": (item) => AccountingModel.getAttributes().requiredDocumentation.value(item).split(', '),
        },
    };

    return (
        <Col lg="12" className='mb-5 p-0 position-relative'>
            <CustomModal 
                isOpen={displayDeleteModal} 
                toggle={() => setDisplayDeleteModal(!displayDeleteModal)}
                body={modalContentDelete} 
                footer={modalFooterDelete}
            />

            <SettingsListHeader
                createText={t('accounting.createAccounting')}
                showCreate={permissionUpdate}
                onCreate={() => setFormPanelId("")}
            />

            <AccountingFormPanel
                isOpen={formPanelId !== null}
                accountingId={formPanelId || null}
                onClose={() => setFormPanelId(null)}
                onSaved={() => setTimeout(() => CustomReload(ADMIN_PATHS.ADMIN_ACCOUNTING_LIST), 1500)}
            />

            <SortableList listOptions={listOptions} />
        </Col>
    );
};

export default AdminAccountingList;